import { useEffect } from "react";

interface DragSelectAutoScrollOptions {
  /**
   * Distance (px) from the top/bottom edge at which auto-scroll kicks in. The
   * closer the pointer gets to the edge, the faster it scrolls.
   */
  edgeSize?: number;
  /** Maximum scroll step per animation frame, in px. */
  maxSpeed?: number;
}

/**
 * Return the caret node/offset under a viewport point, across engines.
 * Chromium exposes `caretRangeFromPoint`; the standardized replacement is
 * `caretPositionFromPoint`. We try both so selection extension keeps working.
 */
function caretFromPoint(
  x: number,
  y: number,
): { node: Node; offset: number } | null {
  const docWithCaret = document as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null;
  };
  if (typeof document.caretRangeFromPoint === "function") {
    const range = document.caretRangeFromPoint(x, y);
    if (range) return { node: range.startContainer, offset: range.startOffset };
  }
  if (typeof docWithCaret.caretPositionFromPoint === "function") {
    const pos = docWithCaret.caretPositionFromPoint(x, y);
    if (pos) return { node: pos.offsetNode, offset: pos.offset };
  }
  return null;
}

/**
 * Auto-scroll a scrollable container while the user drag-selects text past its
 * top/bottom edge.
 *
 * Native browser auto-scroll-during-selection is unreliable inside custom
 * scroll containers (e.g. Radix `ScrollArea`, whose viewport wraps content in a
 * `display:table` box) — the selection just stops at the visible edge and the
 * container stays put, so you can only ever select what's on screen. This hook
 * restores the expected behavior: hold a text-selection drag near the edge and
 * the container scrolls in that direction, extending the selection to the newly
 * revealed text each frame (even while the pointer is stationary).
 *
 * `resolveScrollElement` is a getter rather than a ref so it works with
 * containers whose scroll element is queried lazily and mounted after the first
 * render (again, Radix's viewport). A document-level `pointerdown` listener
 * resolves the element at drag start, so late mounting is a non-issue.
 *
 * **Important:** wrap `resolveScrollElement` in `useCallback` (or define it
 * outside the component) so its identity is stable across renders. The function
 * is an effect dependency, so an unstable reference (e.g. an inline arrow
 * function) tears down and re-registers the document listener on every render.
 */
export function useDragSelectAutoScroll(
  resolveScrollElement: () => HTMLElement | null,
  { edgeSize = 48, maxSpeed = 18 }: DragSelectAutoScrollOptions = {},
): void {
  useEffect(() => {
    let scrollEl: HTMLElement | null = null;
    let pointerY = 0;
    let pointerX = 0;
    let rafId: number | null = null;

    const stopLoop = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const tick = () => {
      rafId = null;
      if (!scrollEl) return;
      const rect = scrollEl.getBoundingClientRect();

      let delta = 0;
      let clampedY = pointerY;
      if (pointerY < rect.top + edgeSize) {
        const intensity = Math.min(
          1,
          (rect.top + edgeSize - pointerY) / edgeSize,
        );
        delta = -Math.ceil(intensity * maxSpeed);
        clampedY = rect.top + 1;
      } else if (pointerY > rect.bottom - edgeSize) {
        const intensity = Math.min(
          1,
          (pointerY - (rect.bottom - edgeSize)) / edgeSize,
        );
        delta = Math.ceil(intensity * maxSpeed);
        clampedY = rect.bottom - 1;
      }

      if (delta !== 0) {
        const before = scrollEl.scrollTop;
        scrollEl.scrollTop = before + delta;
        const scrollMoved = scrollEl.scrollTop !== before;
        // Extend the selection to the text now under the (edge-clamped) pointer
        // so scrolling actually grows the selection instead of just moving the
        // viewport past a frozen selection.
        const clampedX = Math.min(
          Math.max(pointerX, rect.left + 1),
          rect.right - 1,
        );
        const caret = caretFromPoint(clampedX, clampedY);
        const selection = window.getSelection();
        if (caret && selection && selection.rangeCount > 0) {
          try {
            selection.extend(caret.node, caret.offset);
          } catch {
            // extend throws if the caret node isn't in a selectable range
            // (e.g. pointer over a non-text region); ignore and keep scrolling.
          }
        }
        // Once the content boundary is reached scrollTop stops changing; stop
        // the loop so we don't burn a rAF + layout every frame while the
        // pointer sits past the edge with nothing left to scroll. A later
        // pointermove re-arms it.
        if (scrollMoved) {
          rafId = requestAnimationFrame(tick);
        }
      }
    };

    const handlePointerMove = (e: PointerEvent) => {
      pointerY = e.clientY;
      pointerX = e.clientX;
      if (rafId === null) rafId = requestAnimationFrame(tick);
    };

    const endDrag = () => {
      stopLoop();
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
      scrollEl = null;
    };

    const handlePointerDown = (e: PointerEvent) => {
      // Only left-button drags that start inside the scroll container, and
      // only when a text selection is actually possible (skip form controls).
      if (e.button !== 0) return;
      const el = resolveScrollElement();
      if (!el) return;
      const target = e.target as Node | null;
      if (!target || !el.contains(target)) return;

      scrollEl = el;
      pointerY = e.clientY;
      pointerX = e.clientX;
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", endDrag);
      window.addEventListener("pointercancel", endDrag);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      endDrag();
    };
  }, [resolveScrollElement, edgeSize, maxSpeed]);
}
